import { isAnchor, isDependency } from '@/helpers/Anchors';
import { groupAsMap, itemIDs } from '@/helpers/IndexHelpers';
import { sum } from '@/helpers/Math';
import { isMembershipEvent } from '@/helpers/MiscellaneousHelpers';
import { TixTime } from '@/TixTime/TixTime';
import { API } from '@/api/API';
import { fetchCart } from '@/api/Cart';
import { imageData } from './ImageHelpers';
// Do not import the router here. That causes a cyclic dependency that surfaces as a failed Cypress test when rendering
// <LoginForm> on <LoginOrGuestDialog>.
function sumTicketPrices(tickets) {
    const values = tickets.map((ticket) => Number(ticket.face_value));
    return sum(values);
}
export function findStripeFee(items) {
    // API always includes the Stripe cart fees if stripe is enabled on the tenant.
    return items.find((item) => item.gateway_id === 'stripe');
}
export function findStripeOrFreeFee(items) {
    // Necessary to also include free gateways for tenants who don't have stripe enabled.
    return items.find((item) => item.gateway_id === 'stripe' || item.gateway_id === 'free');
}
export function convertCartChangesToModifyApiPayload(changes) {
    const payload = {};
    // Properties are specified in the same order that the API endpoint executes them, for convenience.
    if (changes.create || changes.campaign) {
        payload.create = { channel_id: 'web', campaign: changes.campaign };
    }
    if (changes.remove && changes.remove.length > 0) {
        payload.remove = { tickets: Array.from(changes.remove) };
    }
    if (changes.preloadedPromoCodes && changes.preloadedPromoCodes?.length > 0) {
        payload.apply_code_default = {
            codes: changes.preloadedPromoCodes,
            ignore_errors: true,
            require_benefit: false,
        };
    }
    if (changes.preReservePromoCodes && changes.preReservePromoCodes.codes.length > 0) {
        payload.apply_code_before = changes.preReservePromoCodes;
    }
    if (changes.add && changes.add.length > 0) {
        payload.reserve = { tickets: changes.add, additional_info: changes.additionalInfo };
    }
    if (changes.postReservePromoCodes && changes.postReservePromoCodes.codes.length > 0) {
        payload.apply_code = changes.postReservePromoCodes;
    }
    return payload;
}
export function ticketsToCartItems(groupedTickets, { ticketTypes, ticketGroups, templates, venues, allDaySessionIds, }) {
    return Array.from(groupedTickets).flatMap(([key, tickets]) => {
        const session = 'event_template_id' in key ? key : null;
        const template = 'event_template_id' in key ? templates[key.event_template_id] : key;
        const venue = venues[template.venue_id];
        const sessionId = session?.id;
        // A session can contain tickets from both a flow purchase and an independent purchase.
        // Split by flow_resolution_id so each gets its own cart item.
        return splitByFlowResolution(tickets).map((subTickets) => {
            const flowResolutionId = subTickets[0]?.flow_resolution_id ?? null;
            const baseId = sessionId ? `${template.id},${sessionId}` : template.id;
            const id = flowResolutionId ? `${baseId},${flowResolutionId}` : baseId;
            const byType = Array.from(groupAsMap(subTickets, 'ticket_type_id', ticketTypes));
            const base = {
                id,
                event: template,
                templateId: template.id,
                name: template.name,
                meta: template.meta,
                image: imageData(template, 'cart'),
                allDay: session ? allDaySessionIds.has(session.id) : false,
                faceValue: sumTicketPrices(subTickets),
                isAnchor: isAnchor(template),
                isMembershipEvent: isMembershipEvent(template, subTickets),
                isDependency: isDependency(template, subTickets),
                ticketIds: itemIDs(subTickets),
                flowResolutionId,
                flowName: subTickets[0]?.flow_name,
                flowDefinitionId: subTickets[0]?.flow_definition_id,
                types: byType
                    .map(([type, typeTickets]) => ({
                    type,
                    name: type.name,
                    ticketPrice: Number(type.currency_amount),
                    ticketCount: typeTickets.length,
                    totalPrice: sumTicketPrices(typeTickets),
                    group: ticketGroups[type.ticket_group_id],
                }))
                    .sort((a, b) => a.type._rank - b.type._rank)
                    .sort((a, b) => a.group._rank - b.group._rank),
            };
            if (session) {
                return {
                    ...base,
                    sessionId: session.id,
                    startTime: new TixTime(session.start_datetime, venue.timezone),
                    endTime: new TixTime(session.end_datetime, venue.timezone),
                    allDay: allDaySessionIds.has(session.id),
                };
            }
            else {
                const [ticket] = subTickets;
                return {
                    ...base,
                    validTo: new TixTime(ticket.valid_to, venue.timezone),
                    validFrom: new TixTime(ticket.valid_from, venue.timezone),
                    allDay: false,
                };
            }
        });
    });
}
function splitByFlowResolution(tickets) {
    const groups = new Map();
    for (const ticket of tickets) {
        const key = ticket.flow_resolution_id ?? null;
        const existing = groups.get(key);
        if (existing)
            existing.push(ticket);
        else
            groups.set(key, [ticket]);
    }
    return Array.from(groups.values());
}
export function fetchOrderAndCart(orderId, allTickets) {
    // TODO: Test invalid order IDs or valid order IDs with invalid carts; E.g. pending carts.
    return API.getCached(`ticket_order/${orderId}`).then((orderResponse) => {
        const order = orderResponse.ticket_order._data[0];
        return fetchCartAndHandleExpired(order.cart_id, allTickets).then((cart) => {
            return { cart, order };
        });
    });
}
export function fetchCartAndHandleExpired(cartId, allTickets) {
    const query = { _all_tickets: allTickets };
    return fetchCart(cartId, query).then((cart) => {
        const expiresIn = cart.cart._data[0].expires_in;
        if (expiresIn !== null && expiresIn < 30) {
            return API.delete(`cart/${cartId}`).then(() => {
                return fetchCart(cartId, query);
            });
        }
        return cart;
    });
}
export function timeTillExpiry(expiry) {
    return new TixTime().durationTo(new TixTime(expiry));
}
export function onSubmit(context, router, emit) {
    if (context === 'checkout') {
        emit();
    }
    else {
        router.push('/checkout');
    }
}
export function processScheduledPayment(scheduledPayment, timezone) {
    // Payments are scheduled 12AM next day, so bring back to the previous day to display correctly
    const dueDate = new TixTime(scheduledPayment.due, timezone).subtract(1, 'second');
    const status = dueDate.isAfter(new TixTime(null, timezone)) ? 'Upcoming' : 'Overdue';
    return {
        ...scheduledPayment,
        dueDate: dueDate.format('LONGER_DATE'),
        status,
    };
}
export function processCompletedPayment(completedPayment, timezone) {
    const statusMap = {
        capture: 'Paid',
        refund: 'Refund',
        dispute_opened: 'Dispute Opened',
        dispute_closed: 'Dispute Closed',
        dispute_fee: 'Dispute Fee',
    };
    const occurredOn = new TixTime(completedPayment.occurred_on, timezone);
    return {
        ...completedPayment,
        occurredDate: occurredOn.format('LONGER_DATE'),
        status: statusMap[completedPayment.audit_action],
    };
}
export function getUpcomingAmount(scheduledPayments) {
    return Number(scheduledPayments.find((p) => p.status === 'Upcoming')?.amount ?? 0);
}
export function getOverdueAmount(scheduledPayments) {
    const overduePayments = scheduledPayments.filter((p) => p.status === 'Overdue');
    return sum(overduePayments.map((p) => Number(p.amount)));
}
